import { type Money, abs, sign } from '@/lib/money'
import { KATHMANDU_TZ, formatBsDate } from '@/lib/bsDate'

/**
 * Nepali (and wider South Asian) digit grouping: 12,34,567.89 — lakh/crore
 * grouping, not the western 1,234,567.89.
 *
 * `en-IN`, not `en-NP`: ICU ships no number data for en-NP and silently falls
 * back to plain `en`, which is western grouping. en-IN carries the grouping
 * every NEPSE screen uses, with Latin digits. Pinned by format.test.ts.
 */
const LOCALE = 'en-IN'

const PAISA = new Intl.NumberFormat(LOCALE, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const RUPEES = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 })

const PLAIN = new Intl.NumberFormat(LOCALE)

export interface MoneyFormat {
  /** Drop the paisa — "Rs 14,43,724" for a book value the design shows whole. */
  whole?: boolean | undefined
}

export const formatMoney = (m: Money, opts: MoneyFormat = {}): string =>
  (opts.whole ? RUPEES : PAISA).format(Number(m))

export const formatNPR = (m: Money, opts: MoneyFormat = {}): string => `Rs ${formatMoney(m, opts)}`

const signed = (m: Money, body: string): string => {
  const s = sign(m)
  return s > 0 ? `+${body}` : s < 0 ? `−${body}` : body
}

/** Signed, for a change column: "+2.45" / "−1.10" (U+2212, not a hyphen). */
export const formatSigned = (m: Money, opts: MoneyFormat = {}): string =>
  signed(m, formatMoney(abs(m), opts))

/** Signed with the currency after the sign: "−Rs 3,164", "+Rs 1,49,348". */
export const formatSignedNPR = (m: Money, opts: MoneyFormat = {}): string =>
  signed(m, formatNPR(abs(m), opts))

export const formatPercent = (m: Money): string => `${formatSigned(m)}%`

/** Compact volume for dense tables: 1.20 Cr, 34.50 L, 6.7 K — spaced like the rupee ladder
 *  (`formatRupees`' "12.40 Cr"), so a kitta figure and a rupee figure side by side read as one
 *  system. `null` means the API did not report a figure — not that the volume was zero. */
export const formatVolume = (units: number | null): string => {
  if (units === null || !Number.isFinite(units)) return '—'
  const n = Math.abs(units)
  if (n >= 1e7) return `${(units / 1e7).toFixed(2)} Cr`
  if (n >= 1e5) return `${(units / 1e5).toFixed(2)} L`
  if (n >= 1e3) return `${(units / 1e3).toFixed(1)} K`
  return PLAIN.format(units)
}

export const formatQuantity = (units: number): string => PLAIN.format(units)

/** API AD date or timestamp displayed as a Kathmandu-local BS date. */
export const formatDate = formatBsDate

const CLOCK = new Intl.DateTimeFormat('en-NP', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: KATHMANDU_TZ,
})

/** "11:00 AM" in Kathmandu, whatever zone the device is set to. */
export const formatTime = (iso: string): string => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : CLOCK.format(d)
}

const DIAL = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hourCycle: 'h12',
  timeZone: KATHMANDU_TZ,
})

/**
 * "1:15" — a 12-hour Kathmandu clock with no meridiem, for chart axes where
 * NEPSE's 11:00–15:00 session makes AM/PM noise. Assembled from parts so the
 * separator and the missing meridiem are this file's, not ICU's.
 */
export const formatDialTime = (date: Date): string => {
  const parts = DIAL.formatToParts(date)
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? ''
  return `${part('hour')}:${part('minute')}`
}
